<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.errorMessage" />

        <v-form ref="form" v-model="formValid" @submit.prevent="onSubmit" color="green">
          <v-card-title class="headline">
            {{ $t('@ballanceCorrectionForm/title') }}
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="form.sum"
              color="green darken-1"
              class="px-1"
              type="number"
              name="sum"
              ref="sum"
              :rules="[validateRequired, validateSum]"
              :label="$t('@ballanceCorrectionForm/sumInputLabel')"
              :error-messages="validationError(error.errorField, errorFieldMap.sum)"
            />

            <v-textarea
              v-model="form.description"
              color="green darken-1"
              class="px-1"
              auto-grow
              rows="1"
              name="description"
              :rules="[validateRequired]"
              :label="$t('@ballanceCorrectionForm/descriptionInputLabel')"
              :error-messages="validationError(error.errorField, errorFieldMap.description)"
            />

            <div class="caption grey--text text--darken-1 mt-4">
              {{ $t('@ballanceCorrectionForm/selectDebtHint') }}
            </div>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="debts"
            v-model="selectedDebts"
            item-key="id"
            class="ma-0"
            hide-actions
          >
            <template v-slot:items="props">
              <td>
                <v-checkbox
                  v-model="props.selected"
                  :disabled="debts.length === 1"
                  primary
                  hide-details
                ></v-checkbox>
              </td>
              <td>{{ props.item.start }}</td>
              <td class="text-xs-right">{{ props.item.end }}</td>
              <td class="text-xs-right">{{ props.item.balance }}</td>
            </template>

            <template v-slot:footer>
              <td :colspan="headers.length - 1">
                <strong>{{ $t('@ballanceCorrectionForm/contractBalance') }}, kr</strong>
              </td>
              <td class="column text-xs-right">
                <strong>{{ contractBalance }}</strong>
              </td>
            </template>
          </v-data-table>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="submitting" color="red darken-1" flat @click="reset">
              {{ $t('@ballanceCorrectionForm/cancelButtonLabel') }}
            </v-btn>

            <v-btn type="submit" color="green darken-1" :disabled="submitting" flat>
              {{ $t('@ballanceCorrectionForm/submitButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { validationError, getErrorObject, getErrorMessage } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';
import * as moment from 'moment';

const errorFieldMap = {
  sum: 'sum',
  description: 'description',
};

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
    },
  },

  data() {
    return {
      loading: false,
      debts: [],
      selectedDebts: [],
      balances: [],

      headers: [
        {
          text: '',
          align: 'left',
          sortable: false,
          value: 'id',
        },
        {
          text: this.$t('@ballanceCorrectionForm/periodStart'),
          align: 'left',
          sortable: false,
          value: 'start',
        },
        {
          text: this.$t('@ballanceCorrectionForm/periodEnd'),
          align: 'left',
          sortable: false,
          value: 'end',
        },
        {
          text: this.$t('@ballanceCorrectionForm/balance'),
          align: 'left',
          sortable: false,
          value: 'balance',
        },
      ],

      form: {
        sum: '',
        description: '',
      },
      formValid: true,
      submitting: false,

      validationError,
      errorFieldMap,
      error: {
        errorMessage: '',
        errorField: '',
      },
    };
  },

  watch: {
    selectedDebts() {
      if (this.selectedDebts.length > 1) {
        this.selectedDebts = this.selectedDebts.slice(-1);
      }
    },
  },

  computed: {
    contractBalance() {
      const b = this.balances.find((b) => b.isContractBalance);
      return b ? b.balance : '-';
    },
  },

  methods: {
    async fetch() {
      this.loading = true;

      try {
        const { data: balances } = await this.$http.get(
          `/api/balances?contractId=${this.contract.id}&skip=0&take=50`,
        );

        this.balances = balances || [];

        const {
          data: { data: allDebts },
        } = await this.$http.get(
          `/api/debts?contractId=${
            this.contract.id
          }&from=1970-01-01T00:00:00.000Z&take=9999&skip=0`,
        );

        allDebts.sort((a, b) => b.id - a.id);

        this.debts = [];
        this.selectedDebts = [];

        const debtToRow = (d, b) => ({
          id: d.id,
          start: moment(d.periodFrom).format('YYYY-MM-DD'),
          end: moment(d.periodTill).format('YYYY-MM-DD'),
          balance: b ? b.balance : 'n/a',
        });

        for (const d of allDebts) {
          const b = this.balances.find((b) => b.isDebtBalance && b.debtId === d.id);
          if (b) {
            this.debts.push(debtToRow(d, b));
          }
        }

        if (this.debts.length) {
          // there are processed debts in the contract, select first
          this.selectedDebts = [this.debts.find((d) => Number.isFinite(d.balance))];
        } else if (allDebts.length) {
          // no processed debts, selecting the latest available, nothing is shouwn on UI
          // such debts do not have balance yet, as they are in "planned" status
          this.selectedDebts = this.debts = allDebts.slice(-1).map((d) => debtToRow(d));
        }
      } catch (e) {
        this.error.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    validateRequired(val) {
      const isObject = val === Object(val);
      const isFilled = isObject
        ? Object.keys(val).length > 0
        : val !== undefined && val !== null && val !== '';
      return isFilled || this.$t('@newContractForm/emptyRequiredFieldErrorMessage');
    },

    validateSum(val) {
      const hasProcessedDebts = this.debts.find((d) => Number.isFinite(Number(d.balance)));
      if (hasProcessedDebts) {
        return true;
      }
      if (!Number.isFinite(Number(val))) {
        return 'Sum is not a number';
      }
      if (val < 0) {
        return 'Insurance has no procesed debts yet. Negative correction disallowed';
      }
      return true;
    },

    validateForm() {
      if (!this.selectedDebts.length) {
        this.error.errorMessage = 'No contract debts available yet';
        return false;
      }

      this.error.errorMessage = '';
      this.error.errorField = '';
      this.$refs.form.validate();

      return new Promise((resolve) => {
        this.$nextTick(async () => {
          resolve(this.formValid);
        });
      });
    },

    async onSubmit() {
      try {
        const isValid = await this.validateForm();

        if (!isValid || this.submitting) {
          return;
        }
        this.submitting = true;

        await this.$http.post(`/api/balance-corrections`, {
          ...this.form,
          contractId: this.contract.id,
          debtId: this.selectedDebts[0] && this.selectedDebts[0].id,
        });

        this.error = { errorMessage: '', errorField: '' };
        this.$emit('submitted');
        this.reset();
      } catch (e) {
        this.error = getErrorObject(e);
      } finally {
        this.submitting = false;
      }
    },

    reset() {
      this.$emit('dismiss');
      this.form.sum = '';
      this.form.description = '';
      this.submitting = false;
      this.loading = false;
    },
  },

  mounted() {
    this.fetch();
    this.$nextTick(() => this.$refs.sum.focus());
  },
};
</script>
